/** Help center: frequently asked questions, one section per question. */
import type { InfoDoc } from "../types";

export const help: InfoDoc = {
  es: {
    title: "Centro de ayuda",
    intro: "Las preguntas más frecuentes. ¿No encontrás la respuesta? Escribinos a {email}.",
    sections: [
      { title: "¿Las cartas son originales?", paragraphs: ["Sí. Vendemos solo cartas y productos originales de Pokémon TCG, y los sellados vienen cerrados de fábrica."] },
      {
        title: "¿Cómo sé en qué estado está una carta?",
        paragraphs: ["Cada carta indica su estado (NM, LP, MP, HP o dañada), terminación e idioma. En la [[/guia-de-compra|guía para comprar]] explicamos qué significa cada estado."],
      },
      {
        title: "¿En qué moneda están los precios?",
        paragraphs: ["En dólares. Podés verlos en pesos con el selector US$ / AR$ del encabezado; el valor en pesos usa el dólar blue del día más un 5%."],
      },
      {
        title: "¿Cómo puedo pagar?",
        paragraphs: ["Con Mercado Pago (en pesos, hasta 12 cuotas), con tarjeta internacional o con PayPal (en dólares), por transferencia bancaria (en pesos) o en cripto (USDT). Pagando por transferencia o cripto tenés un descuento: te mostramos los datos y el monto exacto, y nos mandás el comprobante por WhatsApp. No aceptamos efectivo en puntos de pago."],
      },
      {
        title: "¿Necesito una cuenta?",
        paragraphs: ["Para comprar, sí: así podés seguir tus pedidos. Se crea en un minuto con tu email o con Google. Para mirar la tienda no hace falta."],
      },
      {
        title: "¿Por qué mi pedido se canceló solo?",
        paragraphs: ["Reservamos los productos 30 minutos mientras pagás. Si el pago no se completa en ese tiempo, el pedido se cancela y los productos vuelven a estar disponibles. No se te cobra nada."],
      },
      {
        title: "¿Hacen envíos?",
        paragraphs: ["Sí. A CABA ({rate:caba}) y al Gran Buenos Aires (desde {rate:cordon1} según el cordón) lo despachamos dentro de las 48 horas; al resto del país lo coordinamos por WhatsApp. Cada carta viaja en su sleeve (funda), y las de más de US$ 10 además en un toploader. Todo sobre [[/envios|envíos]]."],
      },
      {
        title: "¿Cómo sigo mi pedido?",
        paragraphs: ["En Mi cuenta ves todos tus pedidos y su estado. Cuando lo despachamos te mandamos un email con el número de seguimiento."],
      },
      {
        title: "Me arrepentí de la compra, ¿qué hago?",
        paragraphs: ["Tenés 10 días desde que recibís el producto. Usá el [[/arrepentimiento|botón de arrepentimiento]] y te damos un código de trámite. Más detalles en la [[/reembolsos|política de reembolsos]]."],
      },
      {
        title: "Me llegó algo mal o dañado",
        paragraphs: ["Avisanos lo antes posible a {email} con fotos y el número de pedido. Te lo cambiamos o te devolvemos el dinero, como prefieras. Además tenés la garantía legal: 6 meses en productos sellados y 3 en cartas sueltas, que se consideran usadas."],
      },
      {
        title: "¿Cómo funciona Identificar carta?",
        paragraphs: ["Sacale una foto a la carta (o subí una) en [[/identificar|Identificar carta]]. Leemos el nombre y el número y te mostramos las cartas del catálogo que coinciden. La foto no se guarda."],
      },
      {
        title: "Olvidé mi contraseña",
        paragraphs: ["En Ingresar tocá ¿Olvidaste tu contraseña? y te mandamos un link para crear una nueva. Si entrás con Google, no necesitás contraseña."],
      },
    ],
  },
  en: {
    title: "Help center",
    intro: "The most frequent questions. Can't find the answer? Write to {email}.",
    sections: [
      { title: "Are the cards genuine?", paragraphs: ["Yes. We only sell genuine Pokémon TCG cards and products, and sealed products are factory sealed."] },
      {
        title: "How do I know a card's condition?",
        paragraphs: ["Each card states its condition (NM, LP, MP, HP or damaged), finish and language. The [[/guia-de-compra|buying guide]] explains each condition."],
      },
      { title: "What currency are prices in?", paragraphs: ["US dollars. You can see them in pesos with the US$ / AR$ switch in the header; the peso value uses the day's dólar blue rate plus 5%."] },
      {
        title: "How can I pay?",
        paragraphs: ["With Mercado Pago (pesos, up to 12 installments), an international card or PayPal (dollars), a bank transfer (pesos) or crypto (USDT). Paying by transfer or crypto gets you a discount: we show the details and the exact amount, and you send us the receipt on WhatsApp. We don't take cash at payment outlets."],
      },
      { title: "Do I need an account?", paragraphs: ["To buy, yes, so you can follow your orders. It takes a minute with your email or Google. Browsing doesn't need one."] },
      {
        title: "Why was my order cancelled on its own?",
        paragraphs: ["We hold the products for 30 minutes while you pay. If the payment isn't completed in that time, the order is cancelled and the products become available again. Nothing is charged."],
      },
      { title: "Do you ship?", paragraphs: ["Yes. To Buenos Aires City ({rate:caba}) and Greater Buenos Aires (from {rate:cordon1} depending on the ring) we dispatch within 48 hours; elsewhere we arrange it on WhatsApp. Every card travels in a sleeve, and those over US$10 also in a toploader. More on [[/envios|shipping]]."] },
      { title: "How do I track my order?", paragraphs: ["My account lists your orders and their status. When we ship we email you the tracking number."] },
      {
        title: "I changed my mind, what do I do?",
        paragraphs: ["You have 10 days from delivery. Use the [[/arrepentimiento|withdrawal button]] and you'll get a request code. More in the [[/reembolsos|refund policy]]."],
      },
      { title: "I received something wrong or damaged", paragraphs: ["Let us know as soon as possible at {email} with photos and the order number. We'll replace it or refund you, as you prefer. You also have the legal warranty: 6 months on sealed products and 3 on single cards, which count as used."] },
      {
        title: "How does Identify a card work?",
        paragraphs: ["Take (or upload) a photo of the card in [[/identificar|Identify a card]]. We read its name and number and show the matching catalog cards. The photo isn't stored."],
      },
      { title: "I forgot my password", paragraphs: ["In Sign in, tap Forgot your password? and we'll email you a link to set a new one. If you sign in with Google, you don't need a password."] },
    ],
  },
};
